/* eslint-disable import/no-dynamic-require */
/* eslint-disable global-require */
import { uniq } from 'lodash-es';
import { logger } from '@/lib/logger';

type Params = Record<string, string | number>;

const interpolate = (str: string, params?: Params) => {
  if (process.env.NODE_ENV === 'development') {
    const requiredParams = (str.match(/\{\{ *(\w+) *\}\}/g) || [])
      .map((s) => s.replace('{{', '').replace('}}', '').trim())
      .sort();
    const recievedParameters = Object.keys(params || {}).sort();

    if (uniq(requiredParams).join('#') !== uniq(recievedParameters).join('#')) {
      logger.warn(
        { requiredParams, recievedParameters },
        `Translation for "${str}" expected parameters do not match the received parameters`,
      );
    }
  }

  if (params == null || !Object.entries(params).length) {
    return str;
  }

  return Object.entries(params).reduce(
    (text, [key, value]) =>
      text.replace(
        new RegExp(String.raw`\{\{ *${key} *\}\}`, 'g'),
        (value || '').toString(),
      ),
    str,
  );
};

const normalize = (str: string) => str.replace(/\n/g, ' ').replace(/ +/g, ' ');

let currentLocale = process.env.LOCALE || 'en';

const getLocale = (): string => currentLocale;

// Cache per locale+name to avoid re-requiring on every t() call
const translationCache = new Map<string, Record<string, string>>();

export const setLocale = (locale: string) => {
  if (locale && locale !== currentLocale) {
    currentLocale = locale;
    translationCache.clear();
  }
};

const loadFile = (
  locale: string,
  candidate: string,
): Record<string, string> | null => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const mod = require(`./locales/${locale}/${candidate}.json`);

    return mod.default || mod;
  } catch {
    return null;
  }
};

// A namespace's translations for each locale, e.g. { en: {...}, fr: {...} }.
// The build loader in next.config.js passes these in.
type NamespaceFiles = Record<string, Record<string, string>>;

const loadTranslations = (
  name: string,
  files?: NamespaceFiles,
): Record<string, string> => {
  const locale = getLocale();
  const cacheKey = `${locale}:${name}`;

  if (translationCache.has(cacheKey)) {
    return translationCache.get(cacheKey)!;
  }

  // The Next build passes the files in. Without them (Jest), an explicit
  // namespace (e.g. i18n.init('product')) resolves directly to
  // ./locales/<locale>/product.json; a path-based name walks up to the nearest
  // ancestor file.
  let namespaceTranslations: Record<string, string> = {};

  if (files) {
    namespaceTranslations = files[locale] ?? {};
  } else {
    const candidates = name
      .split('/')
      .map((_, i, a) => a.slice(0, a.length - i).join('/'));

    for (const candidate of candidates) {
      const file = loadFile(locale, candidate);

      if (file) {
        namespaceTranslations = file;
        break;
      }
    }
  }

  // shared.json is merged in as the base layer for every namespace (the
  // fallback). Namespace-specific keys take precedence over shared ones.
  const shared = loadFile(locale, 'shared') ?? {};
  const translations = { ...shared, ...namespaceTranslations };

  translationCache.set(cacheKey, translations);

  return translations;
};

// eslint-disable-next-line no-underscore-dangle
const _namespace = (name: string, files?: NamespaceFiles) => {
  function t(_template: TemplateStringsArray | string, ...params: Params[]) {
    const template =
      typeof _template === 'string' ? [_template] : _template || [];

    if (process.env.NODE_ENV === 'development') {
      const str = `\nString in /pages/${name}:\n\t"${template.join(' ... ')}"`;

      if (
        template.some(
          (i) => i.includes('{{') || i.includes('}}') || /\{[^ ]*\}/.test(i),
        )
      ) {
        throw Error(
          `A translated string contains \`{...}\`, \`{{\`or  \`}}\` which means they are not properly interpolted. Please use \`\${{ key: value }}\` instead.${str}`,
        );
      }
    }

    const str = normalize(
      template
        .flatMap((chunk, i) => {
          if (i === template.length - 1) {
            return chunk;
          }

          return `${chunk}{{${Object.keys(params[i])[0]}}}`;
        })
        .join(''),
    );

    const translations = loadTranslations(name, files);
    const translated = normalize(translations[str] || str);
    const param = params.reduce((acc, obj) => ({ ...acc, ...obj }), {});

    return interpolate(translated, param);
  }

  return t;
};

export default {
  interpolate,
  _namespace,
  setLocale,
  getLocale,
  // The namespace arg is consumed by the build loader (see next.config.js),
  // which rewrites this call to _namespace(...). It's never invoked at runtime.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  init(_ns?: string): ReturnType<typeof _namespace> {
    throw Error(
      'i18n was not initialized by the build loader. Call it at module scope as `const t = i18n[init]()` (optionally with a namespace). See next.config.js.',
    );
  },
};
